import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FiClock, FiCheckCircle, FiXCircle, FiAlertCircle, FiX } from 'react-icons/fi';
import { tasksAPI } from '../api';

function TaskList({ tasks, onUpdate, showParent = false }) {
  const navigate = useNavigate();

  const getStatusIcon = (status) => {
    switch (status) {
      case 'queued':
        return <FiClock />;
      case 'running':
        return <span className="spinner" />;
      case 'completed':
        return <FiCheckCircle />;
      case 'failed':
        return <FiXCircle />;
      case 'cancelled':
        return <FiX />;
      default:
        return <FiAlertCircle />;
    }
  };

  const getLanguageName = (code) => {
    const languages = {
      'en-US': 'English',
      'es-ES': 'Spanish',
      'it-IT': 'Italian',
      'nl-NL': 'Dutch',
      'de-DE': 'German',
    };
    return languages[code] || code;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleCancel = async (e, taskId) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to cancel this task?')) {
      try {
        await tasksAPI.cancelTask(taskId);
        if (onUpdate) onUpdate();
      } catch (error) {
        alert('Failed to cancel task');
      }
    }
  };

  const handleTaskClick = (task) => {
    if (task.status === 'completed') {
      if (task.task_type === 'scrape') {
        navigate(`/scrape/${task.id}`);
      } else {
        navigate(`/translation/${task.id}`);
      }
    }
  };

  const getQueuePosition = (task) => {
    if (task.status !== 'queued') return null;
    
    const queuedTasks = tasks.filter(
      t => t.task_type === task.task_type && t.status === 'queued'
    ).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    
    const position = queuedTasks.findIndex(t => t.id === task.id) + 1;
    return position > 0 ? `Queue position: ${position}` : null;
  };

  if (tasks.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">📋</div>
        <p>No tasks found</p>
      </div>
    );
  }

  return (
    <div className="task-list">
      {tasks.map((task) => (
        <div
          key={task.id}
          className="task-item"
          onClick={() => handleTaskClick(task)}
          style={{ cursor: task.status === 'completed' ? 'pointer' : 'default' }}
        >
          <div className="task-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span className={`task-type task-type-${task.task_type}`}>
                {task.task_type}
              </span>
              <span className={`task-status status-${task.status}`}>
                {getStatusIcon(task.status)}
                {task.status}
              </span>
              {task.status === 'queued' && getQueuePosition(task) && (
                <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  {getQueuePosition(task)}
                </span>
              )}
            </div>
            {task.status === 'queued' && (
              <button
                className="btn btn-danger"
                style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem' }}
                onClick={(e) => handleCancel(e, task.id)}
              >
                Cancel
              </button>
            )}
          </div>
          
          <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
            {task.task_type === 'scrape' && task.url && (
              <div>URL: {task.url.substring(0, 50)}...</div>
            )}
            {task.task_type === 'translation' && task.target_language && (
              <div>Language: {getLanguageName(task.target_language)}</div>
            )}
            {showParent && task.parent_task_id && (
              <div>Parent Task: #{task.parent_task_id}</div>
            )}
            <div>Created: {formatDate(task.created_at)}</div>
            {task.status === 'completed' && (
              <div>Completed: {formatDate(task.completed_at)}</div>
            )}
            {task.status === 'failed' && task.error_message && (
              <div style={{ color: '#ef4444', marginTop: '0.5rem' }}>
                Error: {task.error_message}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default TaskList; 